import { rebaseSignatureAssetUrls, signatureAssetMergeMap } from './signatureAssets';

/**
 * The sender's address as one line: `street, City, State ZIP, Country`, empty
 * parts dropped. #970: the profile stores the address as company_street /
 * company_city / company_state / company_zip / company_country (there is no
 * `address` field), and this mirrors the send side's
 * backend/utils/merge_fields.py::_build_sender_address — keep the two in step.
 */
export function formatSenderAddress(user) {
    if (!user) return '';
    const parts = [];
    if (user.company_street) parts.push(user.company_street);
    const cityState = [user.company_city, user.company_state].filter(Boolean).join(', ');
    if (cityState) parts.push(`${cityState} ${user.company_zip || ''}`.trim());
    if (user.company_country) parts.push(user.company_country);
    return parts.join(', ');
}

/**
 * Build the {{Sender*}} merge map for signature resolution.
 *
 * Shared by PersonalComposePage (inbox lane) and ContactEmailComposer
 * (contact lane) so the two compose surfaces cannot drift — the wrong
 * field names this replaces (user?.full_name / user?.company) silently
 * resolved {{SenderName}}/{{SenderCompany}} to empty strings. Field names
 * verified against the backend User model (runtime/backend/models/auth.py,
 * UserBase: `name`, `company_name`; `org_name` injected at routes/auth.py).
 *
 * #487 — senderEmailVar: the address this message will actually be sent
 * from. The CRM login email is used ONLY when no mailbox can send. A
 * connected mailbox whose address we cannot read merges an EMPTY string
 * rather than the login email: {{SenderEmail}} is baked into the body of
 * a message that really goes out, so a wrong address there is worse than
 * a missing one.
 */
export function buildSenderMergeMap(user, senderEmailVar) {
    return {
        '{{SenderName}}': user?.name || '',
        '{{SenderTitle}}': user?.title || '',
        '{{SenderCompany}}': user?.org_name || user?.company_name || '',
        '{{SenderEmail}}': senderEmailVar || '',
        '{{SenderPhone}}': user?.phone || '',
        '{{SenderLinkedIn}}': user?.linkedin_url || '',
        '{{SenderAddress}}': formatSenderAddress(user),
        ...signatureAssetMergeMap(user),
    };
}

export function resolveSigVars(html, user, senderEmailVar) {
    if (!html) return html;
    let out = rebaseSignatureAssetUrls(html);
    const map = buildSenderMergeMap(user, senderEmailVar);
    for (const [key, val] of Object.entries(map)) out = out.split(key).join(val || '');
    return out;
}
